//ProjectReports.js
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "../../layouts/AdminLayout";
import EngineerLayout from "../../layouts/EngineerLayout";
import API from "../../services/api";
import { FaCloudUploadAlt } from "react-icons/fa";
import {
    Search as SearchIcon, RefreshCw, ClipboardList, Clock, CheckCircle2, XCircle,
    Layers, UploadCloud, Pencil, Trash2, Eye, Download, FileText, Image as ImageIcon,
    User, CalendarDays, Inbox, Check, HardHat, FolderKanban, Send, X,
} from "lucide-react";

/* ─── ROBOTO FONT ────────────────────────────────────────────────────────────── */
const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');`;

/* ─── DESIGN TOKENS ──────────────────────────────────────────────────────────── */
const DS = {
    indigo: "#4F46E5",
    indigoMid: "#6366F1",
    indigoLight: "#EEF2FF",
    indigoDark: "#3730A3",
    teal: "#0D9488",
    tealLight: "#F0FDFA",
    emerald: "#059669",
    emeraldLight: "#ECFDF5",
    amber: "#D97706",
    amberLight: "#FFFBEB",
    rose: "#E11D48",
    roseLight: "#FFF1F2",
    violet: "#7C3AED",
    violetLight: "#F5F3FF",
    orange: "#EA580C",
    orangeLight: "#FFF7ED",
    bg: "#F8FAFC",
    surface: "#FFFFFF",
    surfaceAlt: "#F8FAFC",
    border: "#E2E8F0",
    dark: "#0F172A",
    gray: "#64748B",
    grayLight: "#F1F5F9",
    faint: "#94A3B8",
    /* ProjectList tokens */
    violet2: "#5C2D91",
    violetMid2: "#7B3FB5",
    violetLight2: "#EDE6F7",
    violetFaint: "#F7F3FD",
    borderSolid: "#E8E0F5",
};

/* ─── STAGE CONFIG ───────────────────────────────────────────────────────────── */
const STAGES = [
    { value: "Pre Inspection", icon: SearchIcon, label: "Pre Inspection", description: "Before work begins — site survey, baseline documentation", accent: DS.indigo, accentLight: DS.indigoLight, border: "#C7D2FE" },
    { value: "WIP", icon: HardHat, label: "WIP", description: "Work In Progress — mid-construction milestone reports", accent: DS.amber, accentLight: DS.amberLight, border: "#FDE68A" },
    { value: "Post Site Completion", icon: CheckCircle2, label: "Post Site Completion", description: "Final inspection after all site work is complete", accent: DS.emerald, accentLight: DS.emeraldLight, border: "#A7F3D0" },
];

const STATUS_CONFIG = {
    pending: { label: "Pending", color: "#B45309", bg: "#FFFBEB", border: "#FCD34D", dot: "#F59E0B" },
    approved: { label: "Approved", color: "#065F46", bg: "#ECFDF5", border: "#6EE7B7", dot: "#10B981" },
    rejected: { label: "Rejected", color: "#9F1239", bg: "#FFF1F2", border: "#FECDD3", dot: "#F43F5E" },
    under_review: { label: "Under Review", color: "#5B21B6", bg: "#F5F3FF", border: "#DDD6FE", dot: "#7C3AED" },
};

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const PER_PAGE = 10;

/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT — ALL ORIGINAL STATE, EFFECTS, HANDLERS UNCHANGED
═══════════════════════════════════════════════════════════════════════════════ */
export default function ProjectReports({ layout = "admin" }) {
    const { id: urlProjectId } = useParams();
    const navigate = useNavigate();

    const [currentProject, setCurrentProject] = useState(null);
    const [projects, setProjects] = useState([]);
    const [reports, setReports] = useState([]);
    const [reportCategories, setReportCategories] = useState([]);

    // CHANGE 2: project filter state for admin mode
    const [selectedProjectId, setSelectedProjectId] = useState(urlProjectId || "");

    const [formData, setFormData] = useState({
        project_id: "", report_type: "", report_stage: "",
        title: "", details: "", file: null, images: [],
    });

    const [editingReport, setEditingReport] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [existingEditFile, setExistingEditFile] = useState(null);
    const [showEditForm, setShowEditForm] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [showStageSelect, setShowStageSelect] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [search, setSearch] = useState("");
    const [hovStage, setHovStage] = useState(null);
    const [hov, setHov] = useState(null);
    // CHANGE 8: pagination
    const [page, setPage] = useState(1);
    const [stageFilter, setStageFilter] = useState("");
    const [toast, setToast] = useState(null); // { message, type: "success" | "error" }
    const [refreshing, setRefreshing] = useState(false);
    // NEW — bulk download state
    const [bulkStage, setBulkStage] = useState("");
    const [bulkDownloading, setBulkDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 4000);
        return () => clearTimeout(t);
    }, [toast]);

    const storedRole = (localStorage.getItem("role") || "").toLowerCase();
    const isEngineer = layout === "engineer";
    const Layout = isEngineer ? EngineerLayout : AdminLayout;

    /* ── ALL ORIGINAL useEffects / handlers UNCHANGED ────────────────────── */
    const fetchReportCategories = async () => {
        try {
            let res;
            try {
                res = await API.get("/admin/report-categories");
            } catch {
                res = await API.get("/report-categories");
            }
            const raw = res?.data;
            const cats = raw?.data || raw?.categories || raw?.report_categories || (Array.isArray(raw) ? raw : []);
            if (Array.isArray(cats) && cats.length > 0) {
                setReportCategories(cats);
            }
        } catch (err) {
            console.error("Report categories fetch error:", err);
        }
    };

    useEffect(() => {
        fetchReportCategories();
    }, []);

    const fetchReports = async () => {
        try {
            //    const storedRoleForFetch = (localStorage.getItem("role") || "").toLowerCase();
            // const isAdminUser = localStorage.getItem("is_admin") === "true";
            // const storedRoleForFetch2 = (localStorage.getItem("role") || "").toLowerCase();
            // const isAdminFetch = isAdminUser || storedRoleForFetch2 === "admin" || storedRoleForFetch2 === "super_admin";
            // const api = isAdminFetch
            //     ? "/reports"
            //     : `/reports/managerwise/${localStorage.getItem("id")}`;
            const storedRoleForFetch = (localStorage.getItem("role") || "").toLowerCase();
            const storedTypeForFetch = (localStorage.getItem("type") || "").toLowerCase();
            const isAdminUser = Number(localStorage.getItem("is_admin")) === 1;
            const isAdminFetch = isAdminUser || storedRoleForFetch === "admin" || storedRoleForFetch === "super_admin";
            const isManagerFetch = !isAdminUser && (storedRoleForFetch === "manager" || storedTypeForFetch === "admin");

            const api = isAdminFetch
                ? "/reports"
                : isManagerFetch
                    ? `/reports/managerwise/${localStorage.getItem("id")}`
                    : "/reports/my-reports";   // engineer's own reports
            const res = await API.get(api, {
                headers: {
                    "Cache-Control": "no-cache",
                    "Pragma": "no-cache",
                },
                params: { _t: Date.now() },
            });
            setReports([]);
            setTimeout(() => {
                setReports(res.data.report || res.data.reports || res.data.data || []);
            }, 50);
        } catch (err) {
            console.error("Reports fetch error:", err);
        }
    };
    useEffect(() => {
        if (!isEngineer) {
            API.get("/admin/project-assignments/project-dropdown")
                .then((res) => setProjects(res.data.projects || []))
                .catch((err) => console.error("Projects fetch error:", err));
        }
        fetchReports();
    }, [isEngineer]);

    useEffect(() => {
        if (!urlProjectId) return;
        if (isEngineer) {
            (async () => {
                try {
                    const res = await API.get("/engineers/me/projects");
                    const assignments = res.data?.projects || [];
                    const matchedAssignment = assignments.find(
                        (a) => String(a.id) === String(urlProjectId) ||
                            String(a.real_project_id) === String(urlProjectId) ||
                            String(a.project_id) === String(urlProjectId)
                    );
                    if (!matchedAssignment) {
                        setCurrentProject({ id: null, title: "Assignment not found", project_uid: "—", _error: true });
                        return;
                    }
                    const toPositiveInt = (v) => {
                        if (v === null || v === undefined || v === "") return null;
                        const n = Number(v);
                        return (Number.isFinite(n) && Number.isInteger(n) && n > 0) ? n : null;
                    };
                    const resolvedProjectId =
                        toPositiveInt(matchedAssignment.real_project_id) ||
                        toPositiveInt(matchedAssignment.project_id) ||
                        toPositiveInt(matchedAssignment.projectId) ||
                        toPositiveInt(matchedAssignment.proj_id) || null;
                    if (resolvedProjectId) {
                        const proj = {
                            id: resolvedProjectId,
                            project_uid: matchedAssignment.project_uid || String(resolvedProjectId),
                            title: matchedAssignment.title || matchedAssignment.project_title || matchedAssignment.project || ("Project " + resolvedProjectId),
                            status: matchedAssignment.status,
                            progress: matchedAssignment.progress,
                            start_date: matchedAssignment.start_date,
                            end_date: matchedAssignment.end_date,
                            service_type: matchedAssignment.service_type,
                        };
                        setCurrentProject(proj);
                        setFormData((prev) => ({ ...prev, project_id: resolvedProjectId }));
                        setSelectedProjectId(String(resolvedProjectId));
                    } else {
                        const urlAsInt = Number(urlProjectId);
                        if (Number.isInteger(urlAsInt) && urlAsInt > 0) {
                            const directRes = await API.get(`/projects/${urlAsInt}`);
                            if (directRes.data?.success && directRes.data.project?.id) {
                                const proj = directRes.data.project;
                                setCurrentProject(proj);
                                setFormData((prev) => ({ ...prev, project_id: proj.id }));
                                setSelectedProjectId(String(proj.id));
                                return;
                            }
                        }
                        const uidForFetch = matchedAssignment.project_uid || null;
                        if (!uidForFetch) {
                            setCurrentProject({ id: null, title: "Project not found", project_uid: "—", _error: true });
                            return;
                        }
                        const projRes = await API.get(`/projects/${uidForFetch}`);
                        if (projRes.data?.success && projRes.data.project?.id) {
                            const proj = projRes.data.project;
                            setCurrentProject(proj);
                            setFormData((prev) => ({ ...prev, project_id: proj.id }));
                            setSelectedProjectId(String(proj.id));
                        } else {
                            setCurrentProject({ id: null, title: "Project not found", project_uid: uidForFetch, _error: true });
                        }
                    }
                } catch (err) {
                    console.error("[ProjectReports] project resolution error:", err);
                    setCurrentProject({ id: null, title: "Project not found", project_uid: "—", _error: true });
                }
            })();
        } else {
            API.get(`/projects/${urlProjectId}`)
                .then((res) => {
                    if (res.data?.success) {
                        const proj = res.data.project;
                        setCurrentProject(proj);
                        setFormData((prev) => ({ ...prev, project_id: proj.id }));
                        setSelectedProjectId(String(proj.id));
                    }
                })
                .catch((err) => console.error("[ProjectReports] admin project fetch error:", err));
        }
    }, [urlProjectId, isEngineer]);

const handleStageSelect = (stageValue) => {
    setFormData((prev) => ({ ...prev, report_stage: stageValue }));
    setShowStageSelect(false);
    setShowForm(true);
    fetchReportCategories();
    // NEW: scroll to the upload form once it renders
    setTimeout(() => {
        document.getElementById("upload-form-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
};


    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "images") {
            setFormData((prev) => ({ ...prev, images: Array.from(files) }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: files ? files[0] : value }));
        }
    };

    const handleSave = async () => {
    if (!formData.project_id || !formData.report_type || !formData.title.trim()) {
        setToast({ message: "Please fill in all required fields.", type: "error" }); return;
    }
    if (uploading) return; // prevent double-submit
    setUploading(true);
    try {
        const form = new FormData();
        const matchedProj = projects.find(p => String(p.id) === String(formData.project_id));
        const resolvedProjectId = matchedProj?.assignment_id || formData.project_id;
        form.append("project_id", resolvedProjectId);
        form.append("engineer_id", localStorage.getItem("id"));
        if (urlProjectId) {
            form.append("assignment_id", urlProjectId);
        } else {
            const matchedProject = projects.find(p => String(p.id) === String(formData.project_id));
            if (matchedProject?.assignment_id) {
                form.append("assignment_id", String(matchedProject.assignment_id));
            }
        }
        form.append("report_type", formData.report_type);
        form.append("report_stage", formData.report_stage);
        form.append("title", formData.title.trim());
        form.append("details", formData.details.trim());
        if (formData.file) form.append("file", formData.file);
        formData.images.forEach((img) => form.append("images", img));

        // NEW: explicit long timeout so a slow server doesn't get treated as a
        // 502/Bad Gateway before the upload has actually finished — we wait
        // for the real response before touching the form or refetching.
        const res = await API.post("/reports", form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000, // 2 min — large files / slow server
        });

        if (res.data.success) {
            setToast({ message: "Report uploaded successfully!", type: "success" });
            // Only clear the form and refetch once the upload is confirmed done
            setFormData({ project_id: currentProject?.id ?? "", report_type: "", report_stage: "", title: "", details: "", file: null, images: [] });
            setShowForm(false); setShowStageSelect(false);
            await fetchReports();
        } else {
            setToast({ message: res.data?.message || "Upload failed. Please try again.", type: "error" });
        }
    } catch (err) {
        // NEW: distinguish a genuine timeout/bad-gateway from other errors so
        // the person knows to check whether it actually went through instead
        // of assuming it failed outright
        const isGatewayOrTimeout =
            err.code === "ECONNABORTED" ||
            err.response?.status === 502 ||
            err.response?.status === 504;
        setToast({
            message: isGatewayOrTimeout
                ? "Server took too long to respond. Please check the report list before re-uploading — your file may have saved."
                : (err.response?.data?.message || "Error uploading report"),
            type: "error",
        });
    } finally {
        setUploading(false);
    }
};

    const handleResetForm = () => {
        setFormData({ project_id: currentProject?.id ?? "", report_type: "", report_stage: "", title: "", details: "", file: null, images: [] });
        setShowForm(false); setShowStageSelect(false);
    };

    const handleHeaderButtonClick = () => {
        if (showForm || showStageSelect) { handleResetForm(); } else { setShowStageSelect(true); }
    };

    const handleEditClick = (report) => {
        fetchReportCategories();
        setEditingReport(report);
        setEditFormData({ report_type: report.report_type || "", report_stage: report.report_stage || "", title: report.title || "", details: report.details || "", file: null, images: [] });
        setExistingEditFile(report.file_url || null);
        setShowEditForm(true);
        setTimeout(() => { document.getElementById("edit-form-section")?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 50);
    };

    const handleEditChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "images") {
            setEditFormData((prev) => ({ ...prev, images: Array.from(files) }));
        } else {
            setEditFormData((prev) => ({ ...prev, [name]: files ? files[0] : value }));
        }
    };

    const handleEditSave = async () => {
        if (!editFormData.report_type || !editFormData.title.trim()) { setToast({ message: "Please fill in all required fields.", type: "error" }); return; }
        try {
            const form = new FormData();
            form.append("report_type", editFormData.report_type);
            form.append("report_stage", editFormData.report_stage);
            form.append("title", editFormData.title.trim());
            form.append("details", editFormData.details.trim());
            if (editFormData.file) form.append("file", editFormData.file);
            editFormData.images.forEach((img) => form.append("images", img));
            const res = await API.put(`/reports/${editingReport.id}`, form, { headers: { "Content-Type": "multipart/form-data" } });
            if (res.data.success || res.status === 200) {
                setToast({ message: "Report updated successfully!", type: "success" }); setShowEditForm(false); setEditingReport(null); fetchReports();
            }
        } catch (err) { setToast({ message: err.response?.data?.message || "Error updating report", type: "error" }); }
    };

    const handleEditCancel = () => { setShowEditForm(false); setEditingReport(null); };

    const handleDelete = async (reportId) => {
        if (!window.confirm("Are you sure you want to delete this report? This action cannot be undone.")) return;
        setDeletingId(reportId);
        try {
            const res = await API.delete(`/reports/${reportId}`);
            if (res.data.success || res.status === 200) {
                setToast({ message: "Report deleted successfully.", type: "success" });
                fetchReports();
            } else { setToast({ message: res.data?.message || "Error deleting report", type: "error" }); }
        } catch (err) { setToast({ message: err.response?.data?.message || "Error deleting report", type: "error" }); } finally { setDeletingId(null); }
    };

    const getFileUrl = (fileUrl) => {
        if (!fileUrl) return null;
        if (fileUrl.startsWith("http")) return fileUrl;
        return `https://totalassureplus.com/api/uploads/${fileUrl}`;
    };

    // NEW: fetches the file through the authenticated API instance instead of a
    // bare fetch()/window.open() — the /uploads endpoint requires the same
    // Bearer token every other API.* call in this file already sends. A plain
    // fetch had no way to attach that token, so the server was returning an
    // unauthorized/empty response, which we were mistakenly treating as the
    // real file — hence the blank tab and the corrupted downloaded PDF.
    const fetchFileBlob = async (fileUrl) => {
        if (fileUrl.startsWith("http")) {
            // Already a full external URL (not our own API) — nothing to attach a token to
            const res = await fetch(fileUrl);
            if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
            return res.blob();
        }
        const res = await API.get(`/uploads/${fileUrl}`, { responseType: "blob" });
        return res.data;
    };

   const handleView = async (report) => {
        const images = (() => { try { return JSON.parse(report.image_paths || "[]"); } catch { return []; } })();
        const target = report.file_url || (images.length > 0 ? images[0] : null);

        if (!target) {
            setToast({ message: "No attachments available to view.", type: "error" });
            return;
        }

        try {
            const blob = await fetchFileBlob(target);
            const blobUrl = window.URL.createObjectURL(blob);

            // Same technique as handleDownload — Chrome blocks navigating a
            // pre-opened window.open() tab to a blob: URL created afterwards
            // in a different browsing context, so that approach left the tab
            // stuck on about:blank. Clicking an <a target="_blank"> instead
            // opens it in a new tab reliably.
            const link = document.createElement("a");
            link.href = blobUrl;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Revoke a little later so the new tab has time to actually load it
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
        } catch (err) {
            console.error("View error:", err);
            setToast({ message: "Failed to open attachment.", type: "error" });
        }
    };

    const handleDownload = async (report) => {
        if (!report.file_url) {
            setToast({ message: "No file available to download.", type: "error" });
            return;
        }
        try {
            const blob = await fetchFileBlob(report.file_url);
            const blobUrl = window.URL.createObjectURL(blob);

            const urlParts = report.file_url.split(".");
            const ext = urlParts.length > 1 ? urlParts.pop() : "";
            const safeName = (report.title || "report").replace(/[^a-z0-9_\-]+/gi, "_");
            const fileName = ext ? `${safeName}.${ext}` : safeName;

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download error:", err);
            setToast({ message: "Failed to download file.", type: "error" });
        }
    };

    //new bulk download handler
     // NEW — bulk download by stage
    // NEW — bulk download by stage (forces actual file download via blob, not open)
    const handleBulkDownload = async () => {
        if (!bulkStage) {
            setToast({ message: "Please select a stage first.", type: "error" });
            return;
        }
        const reportsToDownload = projectFilteredReports.filter(
            (r) => (r.report_stage || "").trim() === bulkStage.trim() && r.file_url
        );
        if (reportsToDownload.length === 0) {
            setToast({ message: `No downloadable files found for "${bulkStage}".`, type: "error" });
            return;
        }

        setBulkDownloading(true);
        let successCount = 0;
        let failCount = 0;

        for (const report of reportsToDownload) {
            const url = getFileUrl(report.file_url);
            if (!url) continue;
            try {
                const res = await fetch(url);
                if (!res.ok) throw new Error("Fetch failed");
                const blob = await res.blob();
                const blobUrl = window.URL.createObjectURL(blob);

                // derive filename with correct extension from the actual file_url
                const urlParts = report.file_url.split(".");
                const ext = urlParts.length > 1 ? urlParts.pop() : "";
                const safeName = (report.title || "report").replace(/[^a-z0-9_\-]+/gi, "_");
                const fileName = ext ? `${safeName}.${ext}` : safeName;

                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);

                successCount++;
                // small delay so browser doesn't throttle/block rapid downloads
                await new Promise((resolve) => setTimeout(resolve, 400));
            } catch (err) {
                console.error("Bulk download error for report:", report.id, err);
                failCount++;
            }
        }

        setBulkDownloading(false);

        if (successCount > 0 && failCount === 0) {
            setToast({ message: `Downloaded ${successCount} report(s) successfully.`, type: "success" });
        } else if (successCount > 0 && failCount > 0) {
            setToast({ message: `Downloaded ${successCount} report(s), ${failCount} failed.`, type: "error" });
        } else {
            setToast({ message: "Failed to download reports. Check CORS settings on the file server.", type: "error" });
        }
    };

    /* ── CHANGE 2: filter reports by selected project ── */
    // Engineer: always filtered by currentProject.id (unchanged)
    // Admin: filtered by selectedProjectId dropdown selection
    const projectFilteredReports = (() => {
        if (isEngineer && currentProject?.id) {
            return reports.filter((r) => String(r.project_id) === String(currentProject.id));
        }
        if (!isEngineer && selectedProjectId) {
            // selectedProjectId is string UID like "WP001"
            // projects array has { id: "WP001", project: "...", assignment_id: 109 }
            // r.project_id is the integer assignment id (109)
            const matchedProject = projects.find(p => String(p.id) === String(selectedProjectId));
            if (matchedProject?.assignment_id) {
                return reports.filter((r) => String(r.project_id) === String(matchedProject.assignment_id));
            }
            // fallback: try direct match just in case
            return reports.filter((r) => String(r.project_id) === String(selectedProjectId));
        }
        return reports;
    })();

    const afterStageFilter = stageFilter
        ? projectFilteredReports.filter((r) =>
            (r.report_stage || "").trim() === stageFilter.trim()
        )
        : projectFilteredReports;

    const searchTrimmed = (search || "").trim().toLowerCase();
    const searchTerms = searchTrimmed ? searchTrimmed.split(/\s+/).filter(Boolean) : [];

    const afterSearch = searchTerms.length > 0
        ? afterStageFilter.filter((r) => {
            const combined = [
                r.title,
                r.project_name,
                r.project_title,
                r.project,
                r.project_id,
                r.report_type,
                r.report_stage,
                r.details,
                r.status,
                r.engineer_name,
                r.client_name,
                r.approver_name,
                formatDate(r.created_at),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return searchTerms.every((term) => combined.includes(term));
        })
        : afterStageFilter;

    // CHANGE 8: paginate
    const totalPages = Math.max(1, Math.ceil(afterSearch.length / PER_PAGE));
    const filteredReports = afterSearch.slice((page - 1) * PER_PAGE, page * PER_PAGE);

    const selectedStageConfig = STAGES.find((s) => s.value === formData.report_stage);
    const editStageConfig = STAGES.find((s) => s.value === editFormData.report_stage);

    const TABLE_HEADERS = ["#", "Project", "Stage", "Report Category", "Title", "Details", "Status", "Upload Date", "Approver", "Actions"]; // "Attachments"

    /* ─── KPI counts from full project-filtered list (before search/page) ─── */
    const kpiTotal = projectFilteredReports.length;
    const kpiPending = projectFilteredReports.filter(r => (r.status || "pending").toLowerCase() === "pending").length;
    const kpiApproved = projectFilteredReports.filter(r => (r.status || "").toLowerCase() === "approved").length;
    const kpiRejected = projectFilteredReports.filter(r => (r.status || "").toLowerCase() === "rejected").length;

    /* ════════════════════════════════════════════════════════════════════════
       RENDER
    ════════════════════════════════════════════════════════════════════════ */
    const TOAST_STYLES = {
        success: { bg: DS.emerald, icon: <CheckCircle2 size={16} /> },
        error: { bg: DS.rose, icon: <XCircle size={16} /> },
    };

    return (
        <Layout>
            <style>{`
        ${FONT_IMPORT}

        @keyframes pr-toast-in { from { opacity:0; transform:translateY(12px) scale(0.97); } to { opacity:1; transform:none; } }

      /* CHANGE 4 — Montserrat everywhere */
        .pr-root * { font-family: 'Montserrat', system-ui, sans-serif !important; }
        .pr-uid     { font-family: 'Montserrat', sans-serif !important; }

        /* CHANGE 5 — row hover matching ProjectList */
        .pr-row { transition: background 0.12s ease; }
        .pr-row:hover { background: ${DS.violetFaint} !important; }

        /* Search focus */
        .pr-search-wrap:focus-within {
          border-color: ${DS.indigo} !important;
          box-shadow: 0 0 0 3px rgba(79,70,229,0.1) !important;
        }

        /* Stage card */
        .pr-stage-card { transition: transform 0.18s ease, box-shadow 0.18s ease; }
        .pr-stage-card:hover { transform: translateY(-4px); }

        /* Action button */
        .pr-action-btn { transition: background 0.13s, color 0.13s, transform 0.11s; }
        .pr-action-btn:hover { transform: scale(1.06); }

        /* Upload btn */
        .pr-upload-btn { transition: box-shadow 0.2s, transform 0.14s; }
        .pr-upload-btn:hover:not(:disabled) { transform: translateY(-1px); }

        /* CHANGE 3 — refresh hover */
        .pr-refresh { transition: opacity 0.15s; cursor: pointer; }
        .pr-refresh:hover { opacity: 0.8; }
        @keyframes pr-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @media (max-width: 768px) {
          .pr-page-header { flex-direction: column !important; gap: 14px !important; }
          .pr-kpi-strip   { grid-template-columns: 1fr 1fr !important; }
          .pr-stage-grid  { grid-template-columns: 1fr !important; }
          .pr-form-grid   { grid-template-columns: 1fr !important; }
          .pr-table-scroll { font-size: 12px !important; }
          .pr-hide-md     { display: none !important; }
        }
        @media (max-width: 480px) {
          .pr-kpi-strip { grid-template-columns: 1fr 1fr !important; }
          .pr-hide-sm   { display: none !important; }
        }
      `}</style>

            {toast && (
                <div style={{
                    position: "fixed", top: 24, right: 24, zIndex: 99999,
                    background: TOAST_STYLES[toast.type]?.bg || TOAST_STYLES.success.bg,
                    color: "#fff", padding: "14px 18px", borderRadius: 14,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                    display: "flex", alignItems: "center", gap: 12,
                    minWidth: 280, maxWidth: 380,
                    animation: "pr-toast-in 0.22s ease",
                    fontFamily: "'Montserrat', sans-serif",
                }}>
                    <span style={{ display: "inline-flex", flexShrink: 0 }}>
                        {TOAST_STYLES[toast.type]?.icon || <CheckCircle2 size={16} />}
                    </span>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{toast.message}</div>
                    </div>
                    <button onClick={() => setToast(null)} style={{
                        background: "rgba(255,255,255,0.2)", border: "none", color: "#fff",
                        width: 24, height: 24, borderRadius: "50%", cursor: "pointer",
                        fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}><X size={12} /></button>
                </div>
            )}

            <div className="pr-root" style={{ flex: 1, padding: "28px 32px 40px", overflowY: "auto", background: DS.bg, minHeight: "100vh" }}>

                {/* ── BACK BUTTON ── */}
                <div style={{ marginBottom: 16 }}>
                    <button
                        onClick={() =>
                            isEngineer && urlProjectId
                                ? navigate(`/engineer/project/${urlProjectId}`)
                                : navigate("/admin-dashboard")
                        }
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            background: "#fff",
                            border: "1.5px solid #E2E8F0",
                            borderRadius: 8,
                            padding: "6px 14px",
                            fontSize: 12,
                            fontWeight: 600,
                            color: DS.indigo,
                            cursor: "pointer",
                            fontFamily: "'Montserrat', sans-serif",
                            transition: "all 0.18s",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = DS.indigo;
                            e.currentTarget.style.background = DS.indigoLight;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "#E2E8F0";
                            e.currentTarget.style.background = "#fff";
                        }}
                    >
                        {isEngineer && urlProjectId ? "← Back to Project" : "← Back to Dashboard"}
                    </button>
                </div>



                {/* ── PAGE HEADER ── */}
                <div className="pr-page-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 16 }}>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${DS.indigo},${DS.violet})` }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: DS.faint, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                                {isEngineer ? "Engineer · My Reports" : "Admin · Project Reports"}
                            </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
                            <h1 style={{ fontWeight: 800, fontSize: 28, color: DS.dark, margin: 0, letterSpacing: "-0.5px" }}>
                                Project Reports
                            </h1>
                            {isEngineer && currentProject && !currentProject._error && (
                                <span style={{
                                    background: `linear-gradient(135deg,${DS.indigo},${DS.indigoMid})`,
                                    color: "#fff", fontSize: 9, fontWeight: 700,
                                    padding: "3px 10px", borderRadius: 20, letterSpacing: "0.1em",
                                    textTransform: "uppercase", boxShadow: `0 2px 8px ${DS.indigo}40`,
                                }}>{currentProject.project_uid}</span>
                            )}
                        </div>
                        <p style={{ color: DS.faint, fontSize: 13, margin: 0, fontWeight: 400 }}>
                            {isEngineer && currentProject && !currentProject._error
                                ? `${currentProject.title} — upload, review and track reports`
                                : "Upload, review and track all project reports in one place"}
                        </p>
                    </div>

                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        {/* CHANGE 3 — refresh button exact same as ProjectList.js */}
                        <button
                            className="pr-refresh"
                            onClick={async () => {
                                setRefreshing(true);
                                const minSpin = new Promise(resolve => setTimeout(resolve, 700));
                                await Promise.all([fetchReports(), fetchReportCategories(), minSpin]);
                                setRefreshing(false);
                            }}
                            disabled={refreshing}
                            style={{
                                width: 40, height: 40, borderRadius: 11,
                                border: `1.5px solid ${DS.borderSolid}`,
                                background: DS.surface, color: DS.gray,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0,
                                cursor: refreshing ? "not-allowed" : "pointer",
                                opacity: refreshing ? 0.7 : 1,
                            }}
                            title="Refresh"
                        >
                            <span style={{
                                display: "inline-flex",
                                animation: refreshing ? "pr-spin 0.7s linear infinite" : "none",
                            }}>
                                <RefreshCw size={16} />
                            </span>
                        </button>

                        {/* Upload button
            <button
              className="pr-upload-btn"
              onClick={handleHeaderButtonClick}
              style={{
                background:(showForm || showStageSelect)
                  ? DS.grayLight
                  : `linear-gradient(135deg,${DS.indigo},${DS.indigoMid})`,
                color:(showForm || showStageSelect) ? DS.gray : "#fff",
                border:"none", padding:"11px 22px", borderRadius:12,
                fontSize:13, fontWeight:700, cursor:"pointer",
                boxShadow:(showForm || showStageSelect) ? "none" : `0 4px 14px ${DS.indigo}40`,
                display:"flex", alignItems:"center", gap:8, whiteSpace:"nowrap",
              }}
            >
              {(showForm || showStageSelect) 
  ? <span style={{ fontSize:18, lineHeight:1 }}>✕</span>
  : <FaCloudUploadAlt style={{ fontSize:20 }} />
}
{(showForm || showStageSelect) ? "Close Form" : "Upload Report"}
            </button> */}
                    </div>
                </div>

                {/* ── CHANGE 1: KPI CARDS — exact same card structure as ProjectList.js ── */}
                <div className="pr-kpi-strip" style={{
                    display: "grid", gridTemplateColumns: "repeat(4,1fr)",
                    gap: 16, marginBottom: 24,
                }}>
                    {/* Total Reports — kept as-is */}
                    <div style={{
                        background: DS.surface,
                        borderRadius: 16,
                        border: `1px solid ${DS.borderSolid}`,
                        padding: "20px 22px",
                        display: "flex", alignItems: "center", gap: 14,
                        boxShadow: "0 2px 12px rgba(79,70,229,0.06)",
                        position: "relative", overflow: "hidden",
                    }}>
                        <div style={{
                            position: "absolute", right: -10, top: -10,
                            width: 70, height: 70, borderRadius: "50%",
                            background: `linear-gradient(135deg,${DS.indigoLight},#EEE8FF)`, opacity: 0.6,
                        }} />
                        <div style={{
                            width: 46, height: 46, borderRadius: 13,
                            background: `linear-gradient(135deg,${DS.indigoLight},#EEE8FF)`, border: `1.5px solid #C7D2FE`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 20, flexShrink: 0, position: "relative",
                        }}><ClipboardList size={20} /></div>
                        <div style={{ position: "relative" }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: DS.faint, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                Total Reports
                            </div>
                            <div style={{ fontWeight: 800, fontSize: 28, color: DS.indigo, lineHeight: 1 }}>
                                {kpiTotal}
                            </div>
                        </div>
                    </div>

                    {/* NEW — Bulk Download card, spans the remaining 3 columns */}
                    {/* <div style={{
                        gridColumn: "span 3",
                        display:"none",
                        background: DS.surface,
                        borderRadius: 16,
                        border: `1px solid ${DS.borderSolid}`,
                        padding: "18px 22px",
                        display: "flex", alignItems: "center", gap: 16,
                        flexWrap: "wrap",
                        boxShadow: "0 2px 12px rgba(79,70,229,0.06)",
                    }}>
                        <div style={{
                            width: 46, height: 46, borderRadius: 13,
                            background: DS.indigoLight, border: `1.5px solid #C7D2FE`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 20, flexShrink: 0,
                        }}><Download size={20} color={DS.indigo} /></div>

                        <div style={{ flexShrink: 0, minWidth: 140 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: DS.faint, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                Bulk Download
                            </div>
                            <div style={{ fontSize: 12, color: DS.gray }}>Download all reports by stage</div>
                        </div>

                        <select
                            value={bulkStage}
                            onChange={(e) => setBulkStage(e.target.value)}
                            style={{
                                flex: 1, minWidth: 200, maxWidth: 320,
                                borderRadius: 10, border: `1.5px solid ${DS.borderSolid}`,
                                padding: "10px 14px", fontSize: 13, color: DS.dark,
                                background: DS.surfaceAlt, outline: "none", cursor: "pointer",
                            }}
                            onFocus={e => e.target.style.borderColor = DS.indigo}
                            onBlur={e => e.target.style.borderColor = DS.borderSolid}
                        >
                            <option value="">— Select stage —</option>
                            {STAGES.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>

                        <button
                            onClick={handleBulkDownload}
                            disabled={bulkDownloading || !bulkStage}
                            style={{
                                background: (!bulkStage || bulkDownloading) ? DS.grayLight : `linear-gradient(135deg,${DS.indigo},${DS.indigoMid})`,
                                color: (!bulkStage || bulkDownloading) ? DS.gray : "#fff",
                                border: "none", padding: "10px 20px", borderRadius: 10,
                                fontSize: 13, fontWeight: 700,
                                cursor: (!bulkStage || bulkDownloading) ? "not-allowed" : "pointer",
                                display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap",
                                boxShadow: (!bulkStage || bulkDownloading) ? "none" : `0 4px 14px ${DS.indigo}40`,
                            }}
                        >
                            <Download size={14} />
                            {bulkDownloading ? "Downloading…" : "Download All"}
                        </button>
                    </div> */}
                </div>

                {/* ── CHANGE 2: PROJECT FILTER (admin only) ── */}
                {/* {!isEngineer && (
          <div style={{
            background:DS.surface, borderRadius:16,
            border:`1.5px solid ${DS.borderSolid}`,
            padding:"18px 24px", marginBottom:20,
            boxShadow:"0 2px 10px rgba(79,70,229,0.06)",
            display:"flex", alignItems:"center", gap:16, flexWrap:"wrap",
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
              <div style={{
  width:36, height:36, borderRadius:10, background:DS.indigoLight,
  display:"flex", alignItems:"center", justifyContent:"center",
}}>
  <HardHat size={17} color={DS.indigo} />
</div>
              <div>
                <div style={{ fontWeight:700, fontSize:14, color:DS.dark }}>Filter by Project</div>
                <div style={{ fontSize:11, color:DS.faint, marginTop:1 }}>Select a project to view its reports</div>
              </div>
            </div>
            <select
              value={selectedProjectId}
              onChange={(e) => { setSelectedProjectId(e.target.value); setPage(1); setSearch(""); }}
              style={{
                flex:1, minWidth:240, maxWidth:400,
                borderRadius:10, border:`1.5px solid ${DS.borderSolid}`,
                padding:"10px 14px", fontSize:13, color:DS.dark,
                background:DS.surfaceAlt, outline:"none", cursor:"pointer",
              }}
              onFocus={e => e.target.style.borderColor = DS.indigo}
              onBlur={e  => e.target.style.borderColor = DS.borderSolid}
            >
              <option value="">— Select a project —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.project || p.title || `Project ${p.id}`}</option>
              ))}
            </select>
            {selectedProjectId && (
              <button
                onClick={() => { setSelectedProjectId(""); setPage(1); setSearch(""); }}
                style={{
                  background:DS.grayLight, color:DS.gray,
                  border:`1.5px solid ${DS.border}`, borderRadius:9,
                  padding:"8px 14px", fontSize:12, fontWeight:600, cursor:"pointer",
                  flexShrink:0,
                }}>
                Clear Filter
              </button>
            )}
          </div>
        )} */}

                {/* ── STAGE SELECTION CARDS ── */}
                {showStageSelect && !showForm && (
                    <div style={{ background: DS.surface, borderRadius: 18, border: `1.5px solid ${DS.border}`, overflow: "hidden", marginBottom: 24, boxShadow: `0 6px 28px ${DS.indigo}12` }}>
                        <div style={{ height: 4, background: "linear-gradient(90deg,#4F46E5,#D97706,#059669)" }} />
                        <div style={{ padding: "28px 30px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 26 }}>
                                <div style={{ width: 40, height: 40, borderRadius: 11, background: DS.indigoLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Layers size={19} color={DS.indigo} />
                                </div>                <div>
                                    <div style={{ fontWeight: 700, fontSize: 16, color: DS.dark }}>Select Report Stage</div>
                                    <div style={{ fontSize: 12, color: DS.gray, marginTop: 3 }}>Choose the stage this report belongs to before filling in the details</div>
                                </div>
                            </div>
                            <div className="pr-stage-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 18 }}>
                                {STAGES.map((stage) => {
                                    const isHov = hovStage === stage.value;
                                    return (
                                        <div key={stage.value} className="pr-stage-card"
                                            onClick={() => handleStageSelect(stage.value)}
                                            onMouseEnter={() => setHovStage(stage.value)}
                                            onMouseLeave={() => setHovStage(null)}
                                            style={{
                                                border: `2px solid ${isHov ? stage.accent : stage.border}`,
                                                borderRadius: 16, padding: "24px 22px", cursor: "pointer",
                                                background: isHov ? stage.accentLight : DS.surface,
                                                boxShadow: isHov ? `0 10px 28px ${stage.accent}22` : "0 1px 4px rgba(0,0,0,0.04)",
                                                display: "flex", flexDirection: "column", gap: 14,
                                            }}
                                        >
                                            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                                <div style={{ width: 50, height: 50, borderRadius: 14, background: isHov ? `${stage.accent}22` : stage.accentLight, border: `1.5px solid ${stage.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                    <stage.icon size={24} color={stage.accent} />
                                                </div>                        <div>
                                                    <div style={{ fontWeight: 700, fontSize: 15, color: DS.dark }}>{stage.label}</div>
                                                    <span style={{ display: "inline-block", marginTop: 5, fontSize: 9, fontWeight: 800, color: stage.accent, background: stage.accentLight, border: `1px solid ${stage.border}`, padding: "2px 8px", borderRadius: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>{stage.value}</span>
                                                </div>
                                            </div>
                                            <p style={{ margin: 0, fontSize: 12, color: DS.gray, lineHeight: 1.65 }}>{stage.description}</p>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 14, borderTop: `1px solid ${isHov ? `${stage.accent}30` : stage.border}` }}>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: stage.accent }}>Select this stage</span>
                                                <div style={{ width: 30, height: 30, borderRadius: 9, background: isHov ? stage.accent : `${stage.accent}18`, color: isHov ? "#fff" : stage.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, transition: "background 0.18s, color 0.18s" }}>→</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── UPLOAD FORM ── */}
                {showForm && (
                    <div style={{ background: DS.surface, borderRadius: 18, border: `1.5px solid ${selectedStageConfig?.border || DS.border}`, overflow: "hidden", marginBottom: 24, boxShadow: `0 6px 28px ${selectedStageConfig?.accent || DS.indigo}18` }}>
                        <div style={{ height: 5, background: selectedStageConfig ? `linear-gradient(90deg,${selectedStageConfig.accent},${DS.indigoMid})` : `linear-gradient(90deg,${DS.indigo},${DS.indigoMid})` }} />
                        <div style={{ padding: "26px 30px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
                                <div style={{ width: 40, height: 40, borderRadius: 11, background: selectedStageConfig?.accentLight || DS.indigoLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <UploadCloud size={19} color={selectedStageConfig?.accent || DS.indigo} />
                                </div>                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: 16, color: DS.dark }}>Upload Project Report</div>
                                    <div style={{ fontSize: 12, color: DS.gray, marginTop: 2 }}>Fill in details and attach documents or images</div>
                                </div>
                                {selectedStageConfig && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 14px", borderRadius: 10, background: selectedStageConfig.accentLight, border: `1.5px solid ${selectedStageConfig.border}`, fontSize: 12, fontWeight: 700, color: selectedStageConfig.accent }}>
                                            <selectedStageConfig.icon size={14} /><span>{selectedStageConfig.label}</span>
                                        </div>
                                        <button onClick={() => { if (uploading) return; setFormData((prev) => ({ ...prev, report_stage: "" })); setShowForm(false); setShowStageSelect(true); }}
    disabled={uploading}
    style={{ fontSize: 11, fontWeight: 600, color: DS.gray, background: DS.grayLight, border: `1px solid ${DS.border}`, borderRadius: 8, padding: "6px 11px", cursor: uploading ? "not-allowed" : "pointer", opacity: uploading ? 0.5 : 1 }}>
    Change
</button>
                                    </div>
                                )}
                            </div>
                            <div className="pr-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 18, marginBottom: 20 }}>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.07em" }}>Target Project</label>
                                    {isEngineer ? (
                                        <input type="text" readOnly
                                            value={currentProject?.id ? `${currentProject.title} (${currentProject.project_uid})` : currentProject?._error ? currentProject.title : "Resolving project…"}
                                            style={{ width: "100%", borderRadius: 10, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 13, color: DS.dark, background: DS.grayLight, outline: "none", boxSizing: "border-box", height: "43px", cursor: "default" }} />
                                    ) : (
                                        <select name="project_id" value={formData.project_id} onChange={handleChange}
                                            style={{ width: "100%", borderRadius: 10, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 13, color: DS.dark, background: DS.surfaceAlt, outline: "none", boxSizing: "border-box", height: "43px" }}
                                            onFocus={e => e.target.style.borderColor = DS.indigo} onBlur={e => e.target.style.borderColor = DS.border}>
                                            <option value="">Select Project</option>
                                            {projects.map((p) => (<option key={p.id} value={p.id}>{p.project}</option>))}
                                        </select>
                                    )}
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.07em" }}>Report Type</label>
                                    <select name="report_type" value={formData.report_type} onChange={handleChange}
                                        style={{ width: "100%", borderRadius: 10, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 13, color: DS.dark, background: DS.surfaceAlt, outline: "none", boxSizing: "border-box", height: "43px" }}
                                        onFocus={e => e.target.style.borderColor = DS.indigo} onBlur={e => e.target.style.borderColor = DS.border}>
                                        <option value="">Select Report Type</option>
                                        {formData.report_stage === "Pre Inspection" ? (
                                            <option value="Pre Inspection Report">Pre Inspection Report</option>
                                        ) : formData.report_stage === "Post Site Completion" ? (
                                            <>
                                                <option value="Warranty Report">Warranty Report</option>
                                                <option value="Site Completion Report">Site Completion Report</option>
                                            </>
                                        ) : (
                                            reportCategories.length > 0
                                                ? reportCategories.map((cat) => (<option key={cat.id} value={cat.title}>{cat.title}</option>))
                                                : ["Progress Report", "Quality Report", "Material Report", "Inspection Report"].map((t) => (<option key={t} value={t}>{t}</option>))
                                        )}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.07em" }}>Report Title <span style={{ color: DS.rose }}>*</span></label>
                                    <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Quality Check Phase 1 Complete"
                                        style={{ width: "100%", borderRadius: 10, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 13, color: DS.dark, background: DS.surfaceAlt, outline: "none", boxSizing: "border-box" }}
                                        onFocus={e => e.target.style.borderColor = DS.indigo} onBlur={e => e.target.style.borderColor = DS.border} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.07em" }}>Attach Document</label>
                                    <input type="file" name="file" onChange={handleChange} accept=".pdf,.doc,.docx,.xlsx,.csv,.ppt,.pptx"
                                        style={{ width: "100%", borderRadius: 10, border: `1.5px solid ${DS.border}`, padding: "8px 14px", fontSize: 12, color: DS.dark, background: DS.surfaceAlt, outline: "none", boxSizing: "border-box", height: "43px" }} />
                                </div>
                            </div>
                            <div style={{ marginBottom: 22 }}>
                                <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.07em" }}>Report Details</label>
                                <textarea rows="3" name="details" value={formData.details} onChange={handleChange} placeholder="Summaries, inspector comments, or notes…"
                                    style={{ width: "100%", borderRadius: 10, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 13, color: DS.dark, background: DS.surfaceAlt, outline: "none", boxSizing: "border-box", resize: "vertical", lineHeight: 1.6 }}
                                    onFocus={e => e.target.style.borderColor = DS.indigo} onBlur={e => e.target.style.borderColor = DS.border} />
                            </div>
                            <div style={{ display: "flex", gap: 10 }}>
                                 <button
              onClick={handleSave}
              disabled={uploading}
              style={{
                  background: uploading ? DS.grayLight : `linear-gradient(135deg,${selectedStageConfig?.accent || DS.indigo},${DS.indigoMid})`,
                  color: uploading ? DS.gray : "#fff",
                  border: "none", padding: "11px 28px", borderRadius: 10,
                  fontSize: 13, fontWeight: 700,
                  cursor: uploading ? "not-allowed" : "pointer",
                  boxShadow: uploading ? "none" : `0 4px 14px ${selectedStageConfig?.accent || DS.indigo}40`,
                  display: "flex", alignItems: "center", gap: 8,
              }}>
              {uploading ? (
                  <>
                      <span style={{
                          display: "inline-block", width: 14, height: 14,
                          border: "2px solid rgba(255,255,255,0.35)",
                          borderTopColor: "#fff", borderRadius: "50%",
                          animation: "pr-spin 0.7s linear infinite",
                      }} />
                      Uploading…
                  </>
              ) : (
                  <><FaCloudUploadAlt style={{ fontSize: 16 }} /> Upload Report</>
              )}
          </button>
          <button onClick={handleResetForm} disabled={uploading}
              style={{ background: DS.grayLight, color: DS.gray, border: `1.5px solid ${DS.border}`, padding: "11px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: uploading ? "not-allowed" : "pointer", opacity: uploading ? 0.6 : 1 }}>
              Cancel
          </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── EDIT FORM ── */}
                {showEditForm && editingReport && (
                    <div id="edit-form-section" style={{ background: DS.surface, borderRadius: 18, border: `2px solid ${DS.amber}`, overflow: "hidden", marginBottom: 24, boxShadow: "0 6px 24px rgba(217,119,6,0.14)" }}>
                        <div style={{ height: 5, background: "linear-gradient(90deg,#D97706,#EA580C,#4F46E5)" }} />
                        <div style={{ padding: "26px 30px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
                                <div style={{ width: 40, height: 40, borderRadius: 11, background: DS.amberLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <Pencil size={19} color={DS.amber} />
                                </div>                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: 16, color: DS.dark }}>Edit Report</div>
                                    <div style={{ fontSize: 12, color: DS.gray, marginTop: 2 }}>Editing: <strong style={{ color: DS.dark }}>{editingReport.title}</strong></div>
                                </div>
                                {editStageConfig && (
                                    <div style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "6px 14px", borderRadius: 10, background: editStageConfig.accentLight, border: `1.5px solid ${editStageConfig.border}`, fontSize: 12, fontWeight: 700, color: editStageConfig.accent }}>
                                        <editStageConfig.icon size={14} /><span>{editStageConfig.label}</span>
                                    </div>
                                )}
                            </div>
                            <div className="pr-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 18, marginBottom: 20 }}>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.07em" }}>Report Stage</label>
                                    <select name="report_stage" value={editFormData.report_stage} onChange={handleEditChange}
                                        style={{ width: "100%", borderRadius: 10, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 13, color: DS.dark, background: DS.surfaceAlt, outline: "none", boxSizing: "border-box", height: "43px" }}
                                        onFocus={e => e.target.style.borderColor = DS.amber} onBlur={e => e.target.style.borderColor = DS.border}>
                                        <option value="">Select Stage</option>
                                        {STAGES.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.07em" }}>Report Type</label>
                                    <select name="report_type" value={editFormData.report_type} onChange={handleEditChange}
                                        style={{ width: "100%", borderRadius: 10, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 13, color: DS.dark, background: DS.surfaceAlt, outline: "none", boxSizing: "border-box", height: "43px" }}
                                        onFocus={e => e.target.style.borderColor = DS.amber} onBlur={e => e.target.style.borderColor = DS.border}>
                                        <option value="">Select Report Type</option>
                                        {editFormData.report_stage === "Pre Inspection" ? (
                                            <option value="Pre Inspection Report">Pre Inspection Report</option>
                                        ) : editFormData.report_stage === "Post Site Completion" ? (
                                            <>
                                                <option value="Warranty Report">Warranty Report</option>
                                                <option value="Site Completion Report">Site Completion Report</option>
                                            </>
                                        ) : (
                                            reportCategories.length > 0
                                                ? reportCategories.map((cat) => (<option key={cat.id} value={cat.title}>{cat.title}</option>))
                                                : ["Progress Report", "Quality Report", "Material Report", "Inspection Report"].map((t) => (<option key={t} value={t}>{t}</option>))
                                        )}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.07em" }}>Report Title</label>
                                    <input type="text" name="title" value={editFormData.title} onChange={handleEditChange}
                                        style={{ width: "100%", borderRadius: 10, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 13, color: DS.dark, background: DS.surfaceAlt, outline: "none", boxSizing: "border-box" }}
                                        onFocus={e => e.target.style.borderColor = DS.amber} onBlur={e => e.target.style.borderColor = DS.border} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.07em" }}>Replace Document (optional)</label>

                                    {existingEditFile && !editFormData.file && (
                                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, padding: "8px 12px", background: DS.surfaceAlt, border: `1.5px solid ${DS.border}`, borderRadius: 10 }}>
                                            <div style={{ width: 34, height: 34, borderRadius: 8, background: DS.indigoLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                <FileText size={16} color={DS.indigo} />
                                            </div>
                                            <div style={{ fontSize: 11, color: DS.gray, flex: 1 }}>
                                                Current document on file.<br />Choose a new file below to replace it.
                                            </div>
                                            <a href={getFileUrl(existingEditFile)} target="_blank" rel="noreferrer"
                                                style={{ fontSize: 11, fontWeight: 700, color: DS.indigo, textDecoration: "none", whiteSpace: "nowrap" }}>
                                                View
                                            </a>
                                        </div>
                                    )}
                                    {editFormData.file && (
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 11, color: DS.emerald, fontWeight: 600 }}>
                                            <CheckCircle2 size={14} /> New file selected: {editFormData.file.name}
                                        </div>
                                    )}

                                    <input type="file" name="file" onChange={handleEditChange} accept=".pdf,.doc,.docx,.xlsx,.csv,.ppt,.pptx"
                                        style={{ width: "100%", borderRadius: 10, border: `1.5px solid ${DS.border}`, padding: "8px 14px", fontSize: 12, color: DS.dark, background: DS.surfaceAlt, outline: "none", boxSizing: "border-box", height: "43px" }} />
                                </div>
                            </div>
                            <div style={{ marginBottom: 22 }}>
                                <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.07em" }}>Report Details</label>
                                <textarea rows="3" name="details" value={editFormData.details} onChange={handleEditChange}
                                    style={{ width: "100%", borderRadius: 10, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 13, color: DS.dark, background: DS.surfaceAlt, outline: "none", boxSizing: "border-box", resize: "vertical", lineHeight: 1.6 }}
                                    onFocus={e => e.target.style.borderColor = DS.amber} onBlur={e => e.target.style.borderColor = DS.border} />
                            </div>
                            <div style={{ display: "flex", gap: 10 }}>
                                <button onClick={handleEditSave}
                                    style={{ background: "linear-gradient(135deg,#D97706,#EA580C)", color: "#fff", border: "none", padding: "11px 28px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(217,119,6,0.3)" }}>
                                    <Check size={14} /> Save Changes
                                </button>
                                <button onClick={handleEditCancel}
                                    style={{ background: DS.grayLight, color: DS.gray, border: `1.5px solid ${DS.border}`, padding: "11px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/* ── QUICK UPLOAD BY STAGE CARDS — click a card to jump straight to Upload Report ── */}
{!showForm && (
                <div style={{ background: DS.surface, borderRadius: 18, border: `1.5px solid ${DS.border}`, overflow: "hidden", marginBottom: 24, boxShadow: `0 6px 28px ${DS.indigo}12` }}>
                    <div style={{ height: 4, background: "linear-gradient(90deg,#4F46E5,#D97706,#059669)" }} />
                    <div style={{ padding: "28px 30px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 26 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 11, background: DS.indigoLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Layers size={19} color={DS.indigo} />
                            </div>              <div>
                                <div style={{ fontWeight: 700, fontSize: 16, color: DS.dark }}>Quick Upload by Stage</div>
                                <div style={{ fontSize: 12, color: DS.gray, marginTop: 3 }}>
                                    Click a stage card to jump straight into uploading a report for that stage
                                </div>
                            </div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 18 }}>
                            {STAGES.map((stage) => {
                                const isHov = hovStage === stage.value;
                                return (
                                    <div
                                        key={stage.value}
                                        onClick={() => handleStageSelect(stage.value)}
                                        onMouseEnter={() => setHovStage(stage.value)}
                                        onMouseLeave={() => setHovStage(null)}
                                        style={{
                                            border: `2px solid ${isHov ? stage.accent : stage.border}`,
                                            borderRadius: 16, padding: "24px 22px", cursor: "pointer",
                                            background: isHov ? stage.accentLight : DS.surface,
                                            boxShadow: isHov ? `0 10px 28px ${stage.accent}22` : "0 1px 4px rgba(0,0,0,0.04)",
                                            display: "flex", flexDirection: "column", gap: 14,
                                            transition: "all 0.18s ease",
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                            <div style={{ width: 50, height: 50, borderRadius: 14, background: isHov ? `${stage.accent}22` : stage.accentLight, border: `1.5px solid ${stage.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                <stage.icon size={24} color={stage.accent} />
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 700, fontSize: 15, color: DS.dark }}>{stage.label}</div>
                                                <span style={{ display: "inline-block", marginTop: 5, fontSize: 9, fontWeight: 800, color: stage.accent, background: stage.accentLight, border: `1px solid ${stage.border}`, padding: "2px 8px", borderRadius: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                                    {stage.value}
                                                </span>
                                            </div>
                                        </div>

                                        <p style={{ margin: 0, fontSize: 12, color: DS.gray, lineHeight: 1.65 }}>{stage.description}</p>

                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 14, borderTop: `1px solid ${isHov ? `${stage.accent}30` : stage.border}` }}>
                                            <span style={{ fontSize: 12, fontWeight: 700, color: stage.accent, display: "flex", alignItems: "center", gap: 6 }}>
                                                <Send size={12} /> Click to upload report
                                            </span>
                                            <div style={{ width: 30, height: 30, borderRadius: 9, background: isHov ? stage.accent : `${stage.accent}18`, color: isHov ? "#fff" : stage.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, transition: "background 0.18s, color 0.18s" }}>
                                                →
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
)}

                {/* ── FILTER BY PROJECT ──*/}
                {/* ── TABLE CARD ── */}
                <div style={{ background: DS.surface, borderRadius: 20, border: `1px solid ${DS.borderSolid}`, overflow: "hidden", boxShadow: "0 4px 24px rgba(79,70,229,0.08)" }}>
                    <div style={{ height: 4, background: `linear-gradient(90deg,${DS.indigo},${DS.violet},${DS.rose},${DS.amber})` }} />

                    {/* Toolbar */}
                    <div style={{ borderBottom: `1px solid ${DS.border}`, background: DS.surface }}>

                        {/* Filter by Project — moved inside the table toolbar (admin only) */}
                        {!isEngineer && (
                            <div style={{
                                display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                                padding: "16px 24px", borderBottom: `1px solid ${DS.borderSolid}`,
                                background: DS.surfaceAlt,
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                                    <HardHat size={15} color={DS.indigo} />
                                    <span style={{ fontSize: 12, fontWeight: 700, color: DS.dark, whiteSpace: "nowrap" }}>Filter by Project</span>
                                </div>
                                <select
                                    value={selectedProjectId}
                                    onChange={(e) => { setSelectedProjectId(e.target.value); setPage(1); setSearch(""); }}
                                    style={{
                                        flex: 1, minWidth: 220, maxWidth: 360,
                                        borderRadius: 9, border: `1.5px solid ${DS.borderSolid}`,
                                        padding: "8px 12px", fontSize: 12.5, color: DS.dark,
                                        background: DS.surface, outline: "none", cursor: "pointer",
                                    }}
                                    onFocus={e => e.target.style.borderColor = DS.indigo}
                                    onBlur={e => e.target.style.borderColor = DS.borderSolid}
                                >
                                    <option value="">— Select a project —</option>
                                    {projects.map((p) => (
                                        <option key={p.id} value={p.id}>{p.project || p.title || `Project ${p.id}`}</option>
                                    ))}
                                </select>
                                {selectedProjectId && (
                                    <button
                                        onClick={() => { setSelectedProjectId(""); setPage(1); setSearch(""); }}
                                        style={{
                                            background: DS.grayLight, color: DS.gray,
                                            border: `1.5px solid ${DS.border}`, borderRadius: 8,
                                            padding: "7px 12px", fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                                            flexShrink: 0, whiteSpace: "nowrap",
                                        }}>
                                        Clear Filter
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Title/count, stage tabs, search — same as before */}
                        <div style={{ padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 16, color: DS.dark }}>
                                    {!isEngineer && !selectedProjectId ? "All Reports" : "Report Records"}
                                </div>
                                <div style={{ fontSize: 12, color: DS.faint, marginTop: 3 }}>
                                    Showing <strong style={{ color: DS.dark }}>{afterSearch.length}</strong> of{" "}
                                    <strong style={{ color: DS.dark }}>{projectFilteredReports.length}</strong> report{projectFilteredReports.length !== 1 ? "s" : ""}
                                    {search && <span style={{ color: DS.indigo, fontWeight: 600 }}> · filtered</span>}
                                </div>
                            </div>

                            <div style={{
                                display: "flex", alignItems: "center", gap: 2,
                                background: DS.grayLight, borderRadius: 11, padding: 3,
                                border: `1px solid ${DS.borderSolid}`, flexShrink: 0,
                            }}>
                                <button
                                    onClick={() => { setStageFilter(""); setPage(1); }}
                                    style={{
                                        border: "1px solid transparent", cursor: "pointer",
                                        padding: "8px 16px", borderRadius: 8,
                                        fontSize: 12, fontWeight: 700,
                                        background: stageFilter === "" ? DS.surface : "transparent",
                                        color: stageFilter === "" ? DS.dark : DS.faint,
                                        boxShadow: stageFilter === "" ? "0 1px 3px rgba(15,23,42,0.1)" : "none",
                                        transition: "all 0.15s",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    All
                                </button>
                                {STAGES.map((stage) => {
                                    const isActive = stageFilter === stage.value;
                                    return (
                                        <button
                                            key={stage.value}
                                            onClick={() => { setStageFilter(stage.value); setPage(1); }}
                                            style={{
                                                cursor: "pointer",
                                                padding: "8px 16px", borderRadius: 8,
                                                fontSize: 12, fontWeight: 700,
                                                display: "flex", alignItems: "center", gap: 7,
                                                background: isActive ? stage.accentLight : "transparent",
                                                color: isActive ? stage.accent : DS.faint,
                                                boxShadow: isActive ? `0 1px 3px rgba(15,23,42,0.08)` : "none",
                                                border: isActive ? `1px solid ${stage.border}` : "1px solid transparent",
                                                transition: "all 0.15s",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            <span style={{
                                                width: 7, height: 7, borderRadius: "50%",
                                                background: stage.accent, flexShrink: 0,
                                            }} />
                                            {stage.label}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="pr-search-wrap" style={{
                                display: "flex", alignItems: "center", gap: 9,
                                background: DS.surfaceAlt, border: `1.5px solid ${DS.borderSolid}`,
                                borderRadius: 12, padding: "9px 16px", minWidth: 280,
                                transition: "border-color 0.2s, box-shadow 0.2s",
                            }}>
                                <SearchIcon size={14} color={DS.faint} style={{ flexShrink: 0 }} />
                                <input type="text" placeholder="Search title, project, type…" value={search}
                                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                                    style={{ border: "none", background: "transparent", fontSize: 13, color: DS.dark, outline: "none", width: "100%" }} />
                                {search && (
                                    <button onClick={() => setSearch("")}
                                        style={{ background: DS.indigoLight, border: "none", color: DS.indigo, cursor: "pointer", fontSize: 11, width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0 }}>✕</button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* CHANGE 2: no project selected message */}
                    {!isEngineer && !selectedProjectId && reports.length === 0 ? (
                        <div style={{ padding: "72px 40px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                            <div style={{ width: 72, height: 72, borderRadius: 20, background: `linear-gradient(135deg,${DS.indigoLight},#F0E8FF)`, border: `2px dashed ${DS.faint}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <FolderKanban size={30} color={DS.indigo} />
                            </div>              <div>
                                <div style={{ fontWeight: 700, fontSize: 17, color: DS.dark, marginBottom: 7 }}>Please select a project to view reports</div>
                                <div style={{ fontSize: 13, color: DS.faint, lineHeight: 1.7, maxWidth: 360 }}>
                                    Use the "Filter by Project" dropdown above to choose a project. Reports for that project will appear here.
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* CHANGE 5 — table matching ProjectList.js styling */}
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 1100 }}>
                                    <thead>
                                        <tr style={{ background: DS.violetFaint }}>
                                            {TABLE_HEADERS.map((h, i) => (
                                                <th key={h} style={{
                                                    fontSize: 10, fontWeight: 700, color: DS.gray,
                                                    textTransform: "uppercase", letterSpacing: "0.09em",
                                                    padding: "13px 16px", textAlign: i === 0 ? "center" : "left",
                                                    borderBottom: `2px solid ${DS.borderSolid}`,
                                                    borderTop: `1px solid ${DS.borderSolid}`,
                                                    whiteSpace: "nowrap", background: DS.violetFaint,
                                                }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredReports.length === 0 ? (
                                            <tr>
                                                <td colSpan={TABLE_HEADERS.length} style={{ padding: "64px 40px", textAlign: "center" }}>
                                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, maxWidth: 340, margin: "0 auto" }}>
                                                        <div style={{ width: 68, height: 68, borderRadius: "50%", background: `linear-gradient(135deg,${DS.indigoLight},${DS.violetLight})`, display: "flex", alignItems: "center", justifyContent: "center", border: `1.5px solid #DDD6FE` }}>
                                                            <Inbox size={30} color={DS.indigo} />
                                                        </div>
                                                        <div style={{ fontWeight: 700, fontSize: 16, color: DS.dark }}>
                                                            {stageFilter ? `No "${stageFilter}" reports found` : search ? "No matching reports" : "No reports uploaded yet"}
                                                        </div>
                                                        <div style={{ fontSize: 13, color: DS.faint, lineHeight: 1.65, textAlign: "center" }}>
                                                            {stageFilter ? "Try clearing the stage filter above the table, or upload a new report for this stage." : search ? `No reports match "${search}". Try a different term.` : "Upload the first report using the button above."}
                                                        </div>
                                                        {search && (
                                                            <button onClick={() => setSearch("")}
                                                                style={{ background: DS.indigoLight, color: DS.indigo, border: `1.5px solid #C7D2FE`, borderRadius: 8, padding: "7px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                                                                Clear Search
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredReports.map((report, i) => {
                                                const images = (() => { try { return JSON.parse(report.image_paths || "[]"); } catch { return []; } })();
                                                const stageConf = STAGES.find((s) => s.value === report.report_stage);
                                                const statusKey = (report.status || "pending").toLowerCase().replace(/\s+/g, "_");
                                                const statusConf = STATUS_CONFIG[statusKey] || STATUS_CONFIG.pending;
                                                const isApproved = statusKey === "approved";
                                                const isDeleting = deletingId === report.id;
                                                const hasFile = !!report.file_url;
                                                const hasImages = images.length > 0;
                                                const hasAny = hasFile || hasImages;
                                                const isLast = i === filteredReports.length - 1;
                                                // CHANGE 6 — serial number accounts for pagination
                                                const rowNum = (page - 1) * PER_PAGE + i + 1;

                                                return (
                                                    <tr key={report.id} className="pr-row"
                                                        onMouseEnter={() => setHov(report.id)} onMouseLeave={() => setHov(null)}
                                                        style={{ borderBottom: isLast ? "none" : `1px solid ${DS.borderSolid}` }}
                                                    >
                                                        {/* CHANGE 6 — # column same as ProjectList */}
                                                        <td style={{ padding: "15px 16px", textAlign: "center" }}>
                                                            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 8, background: DS.violetFaint, fontWeight: 700, fontSize: 11, color: DS.faint }}>{rowNum}</span>
                                                        </td>

                                                        {/* Project */}
                                                        <td style={{ padding: "15px 16px", whiteSpace: "nowrap" }}>
                                                            <div style={{ fontSize: 13, fontWeight: 700, color: DS.dark }}>
                                                                {report.project_title || report.project_name || report.project_id || "—"}
                                                            </div>
                                                        </td>

                                                        {/* Stage */}
                                                        <td style={{ padding: "15px 16px", whiteSpace: "nowrap" }}>
                                                            {stageConf ? (
                                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: stageConf.accent, background: stageConf.accentLight, border: `1px solid ${stageConf.border}`, padding: "5px 11px", borderRadius: 20 }}>
                                                                    <stageConf.icon size={12} /> {stageConf.label}
                                                                </span>
                                                            ) : <span style={{ color: DS.faint, fontSize: 12 }}>—</span>}
                                                        </td>

                                                        {/* Category */}
                                                        <td style={{ padding: "15px 16px", whiteSpace: "nowrap" }}>
                                                            <span style={{ display: "inline-block", fontSize: 11, fontWeight: 600, color: DS.violet, background: DS.violetLight, padding: "5px 11px", borderRadius: 20, border: "1px solid #DDD6FE" }}>
                                                                {report.report_type || "—"}
                                                            </span>
                                                        </td>

                                                        {/* Title */}
                                                        <td style={{ padding: "15px 16px", maxWidth: 180 }}>
                                                            <div style={{ fontSize: 13, fontWeight: 600, color: DS.dark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                {report.title}
                                                            </div>
                                                        </td>

                                                        {/* Details */}
                                                        <td style={{ padding: "15px 16px", maxWidth: 200 }}>
                                                            <div style={{ fontSize: 12, color: DS.gray, lineHeight: 1.5 }}>
                                                                {report.details
                                                                    ? report.details.length > 80 ? report.details.slice(0, 80) + "…" : report.details
                                                                    : <span style={{ color: DS.faint }}>—</span>}
                                                            </div>
                                                        </td>

                                                        {/* Status */}
                                                        <td style={{ padding: "15px 16px", whiteSpace: "nowrap" }}>
                                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: statusConf.color, background: statusConf.bg, border: `1.5px solid ${statusConf.border}`, padding: "5px 12px", borderRadius: 20 }}>
                                                                <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusConf.dot, flexShrink: 0 }} />
                                                                {statusConf.label}
                                                            </span>
                                                        </td>

                                                        {/* Upload Date */}
                                                        <td style={{ padding: "15px 16px", whiteSpace: "nowrap" }}>
                                                            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: DS.gray }}>
                                                                <CalendarDays size={13} />{formatDate(report.created_at)}
                                                            </div>
                                                        </td>

                                                        {/* Approver */}
                                                        <td style={{ padding: "15px 16px", whiteSpace: "nowrap" }}>
                                                            {report.approver_name
                                                                ? <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                                                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: DS.emeraldLight, border: `1.5px solid #A7F3D0`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                        <User size={12} color={DS.emerald} />
                                                                    </div>                                    <span style={{ fontSize: 12, fontWeight: 600, color: DS.dark }}>{report.approver_name}</span>
                                                                </div>
                                                                : <span style={{ color: DS.faint, fontSize: 12 }}>—</span>}
                                                        </td>

                                                        {/* Attachments */}
                                                        {/* <td style={{ padding: "15px 16px" }}>
                                                            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                                                {hasFile && (
                                                                    <a href={getFileUrl(report.file_url)} target="_blank" rel="noreferrer"
                                                                        style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: DS.indigo, background: DS.indigoLight, padding: "4px 9px", borderRadius: 7, textDecoration: "none", border: "1px solid #C7D2FE", whiteSpace: "nowrap" }}>
                                                                        <FileText size={11} /> Document
                                                                    </a>
                                                                )}
                                                                {hasImages && (
                                                                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                                                        {images.map((img, idx) => (
                                                                            <a key={idx} href={getFileUrl(img)} target="_blank" rel="noreferrer"
                                                                                style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: DS.teal, background: DS.tealLight, padding: "3px 7px", borderRadius: 6, textDecoration: "none", border: "1px solid #99F6E4", whiteSpace: "nowrap" }}>
                                                                                <ImageIcon size={10} /> {idx + 1}
                                                                            </a>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {!hasAny && <span style={{ color: DS.faint, fontSize: 12 }}>—</span>}
                                                            </div>
                                                        </td> */}

                                                        {/* CHANGE 9 — actions with ProjectList visual language */}
                                                        <td style={{ padding: "15px 16px" }}>
                                                            <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "nowrap" }}>
                                                                {/* View */}
                                                                <button className="pr-action-btn" onClick={() => handleView(report)} disabled={!hasAny} title="View attachment"
                                                                    style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: hasAny ? DS.indigo : DS.faint, background: hasAny ? DS.indigoLight : DS.grayLight, border: `1px solid ${hasAny ? "#C7D2FE" : DS.border}`, padding: "6px 11px", borderRadius: 9, cursor: hasAny ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}>
                                                                    <Eye size={12} />
                                                                </button>
                                                                {/* Download */}
                                                                <button className="pr-action-btn" onClick={() => handleDownload(report)} disabled={!hasFile} title="Download file"
                                                                    style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: hasFile ? DS.teal : DS.faint, background: hasFile ? DS.tealLight : DS.grayLight, border: `1px solid ${hasFile ? "#99F6E4" : DS.border}`, padding: "6px 11px", borderRadius: 9, cursor: hasFile ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}>
                                                                    <Download size={12} />
                                                                </button>
                                                                {/* Edit */}
                                                                {!isApproved && (
                                                                    <button onClick={() => handleEditClick(report)} title="Edit report"
                                                                        style={{
                                                                            background: DS.indigoLight, color: DS.indigo,
                                                                            border: "1px solid #C7D2FE",
                                                                            padding: "7px 16px", borderRadius: 10,
                                                                            fontSize: 12, fontWeight: 700, cursor: "pointer",
                                                                            transition: "all 0.15s", whiteSpace: "nowrap",
                                                                        }}
                                                                        onMouseEnter={e => { e.currentTarget.style.background = DS.indigo; e.currentTarget.style.color = "#fff"; }}
                                                                        onMouseLeave={e => { e.currentTarget.style.background = DS.indigoLight; e.currentTarget.style.color = DS.indigo; }}
                                                                    ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg></button>
                                                                )}
                                                                {/* Delete */}
                                                                {!isApproved && (
                                                                    <button className="pr-action-btn" onClick={() => handleDelete(report.id)} disabled={isDeleting} title="Delete report"
                                                                        style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: DS.rose, background: DS.roseLight, border: "1px solid #FECDD3", padding: "6px 11px", borderRadius: 9, cursor: isDeleting ? "not-allowed" : "pointer", opacity: isDeleting ? 0.6 : 1, whiteSpace: "nowrap" }}>
                                                                        {isDeleting ? <Clock size={12} /> : <Trash2 size={12} />} {isDeleting ? "…" : ""}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* CHANGE 8 — pagination matching ProjectList.js */}
                            {afterSearch.length > 0 && (
                                <div style={{ padding: "14px 24px", borderTop: `1px solid ${DS.borderSolid}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: "#FAFBFF", flexWrap: "wrap", gap: 12 }}>
                                    <span style={{ fontSize: 12, color: DS.faint, fontWeight: 500 }}>
                                        Showing{" "}
                                        <strong style={{ color: DS.dark }}>{Math.min((page - 1) * PER_PAGE + 1, afterSearch.length)}–{Math.min(page * PER_PAGE, afterSearch.length)}</strong>
                                        {" "}of{" "}
                                        <strong style={{ color: DS.dark }}>{afterSearch.length}</strong>
                                        {" "}reports
                                    </span>
                                    <div style={{ display: "flex", gap: 6 }}>
                                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                                            style={{ height: 34, padding: "0 14px", borderRadius: 10, border: `1.5px solid ${page === 1 ? DS.borderSolid : DS.indigo}`, background: page === 1 ? DS.surface : DS.indigoLight, color: page === 1 ? DS.faint : DS.indigo, cursor: page === 1 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>
                                            ‹ Prev
                                        </button>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                                            .slice(Math.max(0, page - 3), Math.min(totalPages, page + 2))
                                            .map((n) => (
                                                <button key={n} onClick={() => setPage(n)}
                                                    style={{ width: 34, height: 34, borderRadius: 10, border: `1.5px solid ${n === page ? DS.indigo : DS.borderSolid}`, background: n === page ? `linear-gradient(135deg,${DS.indigo},${DS.indigoMid})` : DS.surface, color: n === page ? "#fff" : DS.faint, fontSize: 12, fontWeight: 700, cursor: "pointer", boxShadow: n === page ? `0 2px 8px ${DS.indigo}40` : "none" }}>
                                                    {n}
                                                </button>
                                            ))}
                                        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                            style={{ height: 34, padding: "0 14px", borderRadius: 10, border: `1.5px solid ${page === totalPages ? DS.borderSolid : DS.indigo}`, background: page === totalPages ? DS.surface : DS.indigoLight, color: page === totalPages ? DS.faint : DS.indigo, cursor: page === totalPages ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>
                                            Next ›
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

            </div>
        </Layout>
    );
}